/**
 * MKS-341 — the ONE due-date ladder.
 *
 * Four consumers read it (the board card chip, the slide-over's read row, the
 * board `Due:` filter and the board due-date sort), and a second
 * implementation of "is this overdue" would drift exactly the way
 * `taskCardSerializer`'s header describes: the card would say amber while the
 * filter said overdue. So the comparison lives here once.
 *
 * ## Why day strings, not Dates
 *
 * `TaskCard.dueDate` is a calendar day (`YYYY-MM-DD`) — the backend emits the
 * `@db.Date` column, never an ISO instant, so that "overdue" cannot flip with
 * the reader's timezone. Every comparison below therefore stays in that same
 * vocabulary: zero-padded day strings compare lexicographically in
 * chronological order, which `boardFilters.matchesDateFilter` already relies
 * on for the created/updated slots. Parsing to a Date and subtracting would
 * reintroduce the timezone question this shape exists to avoid.
 *
 * "Today" is the reader's LOCAL calendar day, matching
 * `boardFilters.toLocalDayKey`'s D-08 contract: the user compares the due date
 * against the day they believe it is, not against UTC.
 */

/**
 * - `overdue`   — the day has passed. Never returned for a terminal ticket.
 * - `due-soon`  — due today, tomorrow or the day after (the ticket's "within
 *                 2 days" amber band). Never returned for a terminal ticket.
 * - `upcoming`  — a due date further out, or any due date on a terminal ticket.
 */
export type TaskDueState = 'overdue' | 'due-soon' | 'upcoming';

/** The ticket's amber band, in whole days from today (inclusive). */
export const DUE_SOON_DAYS = 2;

/** Local-time `YYYY-MM-DD` for a timestamp. Mirrors `boardFilters.toLocalDayKey`. */
export function localDayKey(at: Date = new Date()): string {
  const year = at.getFullYear();
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const day = String(at.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** `2026-10-20` + 2 → `2026-10-22`. Calendar-correct across month/year ends. */
export function addDaysToDayKey(dayKey: string, days: number): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return dayKey;
  // UTC constructor + UTC getters: the arithmetic must not be perturbed by a
  // DST transition in the reader's zone, which `setDate` on a local Date would
  // do (23- and 25-hour days). The result is still a plain calendar day.
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

export interface TaskDueStateOptions {
  /**
   * Whether the ticket sits in a terminal column (Done / Archived /
   * Cancelled). A terminal ticket is NEVER overdue or due-soon — finished work
   * does not run late. Mirrors the `isTerminal` suppression
   * `taskStalenessEmoji` already applies to the 💩 age emoji on the same card.
   */
  isTerminal?: boolean | undefined;
  /** Injectable "now" for tests; defaults to the real clock. */
  now?: Date | undefined;
}

/**
 * The due state of a ticket, or `null` when it has no due date — which is the
 * default for every ticket and must render exactly as it did before this
 * field existed.
 *
 * A malformed value also yields `null` rather than a guessed state: the wire
 * type allows `string`, and painting a card red because of an unparseable
 * string would be worse than showing nothing.
 */
export function taskDueState(
  dueDate: string | null | undefined,
  { isTerminal, now }: TaskDueStateOptions = {},
): TaskDueState | null {
  if (!dueDate || !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return null;
  // Deliberately BEFORE the comparison, not folded into the caller: every
  // surface that shows a due chip must suppress the warning states the same
  // way, and a caller that forgets is how a Done column turns red.
  if (isTerminal) return 'upcoming';

  const today = localDayKey(now ?? new Date());
  if (dueDate < today) return 'overdue';
  if (dueDate <= addDaysToDayKey(today, DUE_SOON_DAYS)) return 'due-soon';
  return 'upcoming';
}

/**
 * `2026-10-20` → `20 Oct`, or `20 Oct 2026` when the due date is not in the
 * current year — a bare `20 Oct` on a ticket due next year reads as this year.
 *
 * English per the UI-language rule. Formatted from the parts rather than
 * `toLocaleDateString` on a parsed Date so a `YYYY-MM-DD` can never render as
 * the previous day in a negative-offset timezone.
 */
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function formatDueDate(dueDate: string, now: Date = new Date()): string {
  const [year, month, day] = dueDate.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return dueDate;
  const monthName = MONTH_NAMES[month - 1];
  if (!monthName) return dueDate;
  const label = `${day} ${monthName}`;
  return year === now.getFullYear() ? label : `${label} ${year}`;
}

/**
 * The chip's tooltip / accessible label: the state said in words, so the
 * colour is never the only carrier of the meaning.
 */
export function dueDateLabel(dueDate: string, state: TaskDueState, now: Date = new Date()): string {
  const formatted = formatDueDate(dueDate, now);
  const today = localDayKey(now);
  if (state === 'overdue') return `Overdue — was due ${formatted}`;
  if (dueDate === today) return `Due today (${formatted})`;
  if (dueDate === addDaysToDayKey(today, 1)) return `Due tomorrow (${formatted})`;
  return `Due ${formatted}`;
}
